import { AxiosError, AxiosResponse } from "axios";

/**
 * Resolves with the `AxiosError` instead of rejecting.
 *
 * The response error interceptor used to do this for every URL outside a
 * hardcoded allowlist, so the imperative call sites in this app were written
 * against it: they `await` the request and then test `res.status === 200`,
 * which fails closed because `AxiosError.status` only exists when an HTTP
 * response actually arrived.
 *
 * Restoring the axios contract in the interceptor would have turned every one
 * of those sites into an unhandled rejection, so the behaviour was moved here
 * instead, unchanged. Wrapping a call in `swallow` is therefore a no-op today
 * and a marker for later: each one is a call site that still treats a failed
 * request as a value rather than an error.
 *
 * New code should not use this. Read the request through react-query, or
 * handle the rejection with try/catch.
 */
/**
 * Generic over the whole resolved type rather than over the response body,
 * because a handful of routes are already declared as
 * `Promise<AxiosResponse<T> | AxiosError>` - an honest description of what the
 * interceptor used to hand back. Narrowing to `AxiosResponse<T>` here would
 * reject those, and widening every result to a union would break the
 * `res.data` reads at the call sites. Preserving the caller's own type does
 * neither.
 */
export const swallow = <T extends AxiosResponse<unknown> | AxiosError>(
  request: Promise<T>
): Promise<T> => request.catch((error: AxiosError) => error as unknown as T);
